<template>
  <div>
    <page-header
      :actions="headerActionConfig"
      left-side-class="col-xl-4"
      :page="pageHeaderConfig"
      right-side-class="col-xl-8 align-items-end d-flex pb-2 justify-content-end"
    />
    <validation-observer ref="trainingDetailsForm" novalidate tag="form">
      <div class="mb-2">
        <section-title :has-line="true" title="Información General" />
        <div class="row ml-1">
          <div class="col-md-2 mb-3">
            <validation-provider v-slot="{ errors }" rules="required">
              <custom-date-picker
                v-model="model.date"
                class="w-100"
                :class-container="''"
                custom-error-msg="Fecha requerida."
                :disabled="isReadOnly"
                :error="errors[0]"
                label="Fecha:"
                :max-date="new Date()"
                placeholder="DD/MM/YYYY"
                @input="onDateChange"
              />
            </validation-provider>
          </div>
          <div class="col-md-4">
            <validation-provider v-slot="{ errors }" rules="required">
              <custom-drop-down
                v-model="model.selectedActivity"
                custom-error-msg="Favor seleccionar actividad."
                :disabled="!isAdding"
                :error="errors[0]"
                fieldkey="id"
                fieldname="name"
                label="Actividad:"
                :options="activities"
                placeholder="Seleccione"
                @input="onActivityChange"
              />
            </validation-provider>
          </div>
          <div v-if="showMunicipality" class="col-md-3">
            <validation-provider v-slot="{ errors }" rules="required">
              <custom-drop-down
                v-model="model.selectedMunicipality"
                class="w-100"
                custom-error-msg="Favor seleccionar municipio."
                :disabled="isReadOnly"
                :error="errors[0]"
                fieldkey="id"
                fieldname="name"
                label="Municipio:"
                :options="municipalities"
                placeholder="Seleccione"
              />
            </validation-provider>
          </div>
          <div v-if="showPlace" class="col-md-3">
            <validation-provider v-slot="{ errors }" rules="required">
              <custom-input
                v-model="model.place"
                custom-error-msg="Favor escribir un lugar."
                :disabled="isReadOnly"
                :error="errors[0]"
                label="Lugar:"
                type="text"
              />
            </validation-provider>
          </div>
          <div v-if="showHour" class="col-md-2">
            <validation-provider v-slot="{ errors }" rules="required">
              <custom-time-picker
                v-model="model.startHour"
                class="custom-datetime-picker-container"
                :class-container="''"
                custom-error-msg="Hora requerida."
                :disabled="isReadOnly"
                :error="errors[0]"
                :is-menu-right="false"
                label="Hora Comienzo:"
              />
            </validation-provider>
          </div>
          <div v-if="showDuration" class="col-md-4">
            <validation-provider v-slot="{ errors }" rules="required">
              <custom-drop-down
                v-model="model.selectedDuration"
                class="w-100"
                custom-error-msg="Favor seleccionar duración."
                :disabled="isReadOnly"
                :error="errors[0]"
                fieldkey="id"
                fieldname="name"
                label="Duración:"
                :options="durations"
                placeholder="Seleccione"
              />
            </validation-provider>
          </div>
          <div v-if="showSpecialist" class="col-md-3">
            <validation-provider v-slot="{ errors }" rules="required">
              <custom-drop-down
                v-model="model.selectedSpecialist"
                class="w-100"
                custom-error-msg="Favor seleccionar especialista."
                :disabled="isReadOnly"
                :error="errors[0]"
                fieldkey="id"
                fieldname="name"
                label="Especialista:"
                :options="specialists"
                placeholder="Seleccione"
              />
            </validation-provider>
          </div>
          <div v-if="showParticipants" class="col-md-3">
            <label class="font-14 font-weight-normal mt-2 mb-0">
              Participantes:
            </label>
            <div
              v-for="(item, index) in participants"
              :key="`participant-chk-${index}`"
              class="d-flex flex-wrap"
            >
              <checkbox
                v-model="item.selected"
                :disabled="isReadOnly"
                :text="item.name"
              />
              <validation-provider
                v-slot="{ errors }"
                :rules="!participants.some((p) => p.selected) ? 'required' : ''"
              >
                <error
                  v-if="errors[0] && index == participants.length - 1"
                  :message="'Favor seleccionar al menos un participante.'"
                  variant="no-border"
                />
              </validation-provider>
            </div>
          </div>

          <div v-if="showCost" class="col-md-2 mb-3">
            <validation-provider
              v-slot="{ errors }"
              name="costo"
              rules="required|min_value:1"
            >
              <custom-input
                v-model="model.cost"
                class="custom-input__extended"
                :disabled="isReadOnly"
                :error="errors[0]"
                label="Costo:"
                :max="999999"
                :min="1"
                placeholder="Escribir Costo"
                prepend-icon="fa fa-"
                prepend-icon-text="$"
                type="number"
                @keypress.enter.prevent=""
              />
            </validation-provider>
          </div>

          <div v-if="showComment" class="col-12 mt-2">
            <validation-provider v-slot="{ errors }" rules="required">
              <custom-text-area
                id="comment"
                v-model="model.comment"
                custom-error-msg="Favor escribir un comentario."
                :disabled="isReadOnly"
                :error="errors[0]"
                label="Comentario:"
                :maxlength="500"
                name="comment"
                :rows="3"
                :show-char-count="true"
              />
            </validation-provider>
          </div>
        </div>
      </div>

      <div v-if="showStudentSearch && !isReadOnly" class="mt-3">
        <div class="p-3 ml-3 rounded bg-secondary-3">
          <div class="row">
            <div class="col-md-5 border-right">
              <div class="d-flex flex-row align-items-center">
                <em
                  class="fas fa-user-alt rounded-circle shadow p-2 color-primary-2 mr-2"
                />
                <span class="color-black font-16 font-weight-bold">
                  Búsqueda de Estudiante
                </span>
              </div>
              <div class="mt-4 ml-4">
                <validation-observer ref="studentSearch" novalidate tag="form">
                  <validation-provider
                    v-slot="{ errors }"
                    :rules="selectedStudent ? '' : 'required'"
                  >
                    <custom-input
                      v-model="searchStudentSie"
                      class="custom-input__extended"
                      custom-error-msg="Campo requerido - Debe buscar y seleccionar un estudiante."
                      :error="errors[0]"
                      placeholder="Escribir Número de SIE"
                      prepend-icon="fa fa-search"
                      :prepend-icon-click="onSearchStudentHandler"
                      :prepend-icon-left="false"
                      prepend-icon-text="Buscar"
                      prepend-icon-type="success"
                      type="text"
                      @keypress.enter.prevent=""
                    />
                  </validation-provider>
                </validation-observer>
              </div>
            </div>
            <div v-if="student.studentId" class="col-md-4 pl-5">
              <div class="col-secondary-6 font-weight-bold font-16">
                Resultado
              </div>
              <div class="d-flex flex-column mt-2">
                <div class="d-flex align-items-center">
                  <span class="color-secondary-6 mr-2">Nombre:</span>
                  <span class="color-black font-16 font-weight-bold">
                    {{ student.fullName || '-' }}
                  </span>
                </div>
                <div class="d-flex align-items-center">
                  <span
                    class="color-secondary-6 font-14 font-weight-normal mr-2"
                  >
                    Edad:
                  </span>
                  <span class="color-black font-16 font-weight-normal">
                    {{ student.age || '-' }}
                  </span>
                </div>
                <div class="d-flex align-items-center">
                  <span
                    class="color-secondary-6 font-14 font-weight-normal mr-2"
                  >
                    SIE:
                  </span>
                  <span class="color-black font-16 font-weight-normal">
                    {{ student.studentId || '-' }}
                  </span>
                </div>
                <div class="d-flex align-items-center">
                  <span
                    class="color-secondary-6 font-14 font-weight-normal mr-2"
                  >
                    Registro:
                  </span>
                  <span class="color-black font-16 font-weight-normal">
                    {{ student.regNumber || '-' }}
                  </span>
                </div>
              </div>
            </div>
            <div
              v-if="student.studentId"
              class="col-md-3 d-flex justify-content-end align-items-center"
            >
              <action-button
                i="fas fa-check"
                variant="outline-success"
                @click="onStudentSelection(student)"
              >
                Seleccionar
              </action-button>
            </div>
          </div>
        </div>
      </div>
      <div class="mt-1">
        <div class="ml-3">
          <validation-observer ref="selectedstudent" novalidate tag="form">
            <validation-provider
              v-slot="{ errors }"
              :rules="
                searchStudentSie != null &&
                student.studentSie == null &&
                selectedStudent == null
                  ? 'required'
                  : ''
              "
            >
              <error
                v-if="errors[0]"
                :message="'Debe buscar y seleccionar un estudiante.'"
                variant="no-border"
              />
            </validation-provider>
          </validation-observer>
        </div>
      </div>
      <custom-callout
        v-if="selectedStudent != null"
        class="ml-3 mt-1 custom-callout"
        :has-toggle="false"
      >
        <div class="ml-4 bg-secondary-3 rounded mb-3">
          <div class="d-flex flex-row align-items-center">
            <span class="color-black font-16 font-weight-bold">
              Estudiante seleccionado
            </span>
          </div>
          <div class="row mt-2">
            <div class="col">
              <div class="color-secondary-6 font-14">Número de Sie:</div>
              <div class="font-weight-normal color-black font-16">
                {{ selectedStudent.studentId }}
              </div>
            </div>
            <div class="col">
              <div class="color-secondary-6 font-14">Nombre:</div>
              <div class="font-weight-normal color-black font-16">
                {{ selectedStudent.fullName }}
              </div>
            </div>
            <div class="col">
              <div class="color-secondary-6 font-14">Edad:</div>
              <div class="font-weight-normal color-black font-16">
                {{ selectedStudent.age }}
              </div>
            </div>
            <div class="col">
              <div class="color-secondary-6 font-14">Número de Registro:</div>
              <div class="font-weight-normal color-black font-16">
                {{ selectedStudent.regNumber }}
              </div>
            </div>
            <div class="col d-flex justify-content-end align-items-center">
              <action-button
                v-if="!isReadOnly"
                class="pl-4 pr-4"
                i="fas fa-times-circle"
                variant="outline-danger"
                @click="onStudentSelectionDelete()"
              >
                Eliminar
              </action-button>
            </div>
          </div>
        </div>
      </custom-callout>

      <div v-if="selectedStudent != null && isCompuActivity" class="mt-4">
        <section-title
          :has-line="true"
          title="Asistencia de estudiante en COMPU"
        />
        <div class="col-12 ml-2 p-0 my-2">
          <span class="font-weight-normal font-16 ">
            ¿Estudiante asistió a COMPU?
          </span>
          <validation-provider v-slot="{ errors }" rules="required">
            <custom-radio
              v-model="model.studentAssistance"
              class="custom-radio__extended p-0 m-0"
              container-class="p-0 mt-2"
              custom-error-msg="Marque sí o no."
              :disabled="isReadOnly"
              :error="Boolean(errors[0])"
              is-inline
              name="question-1"
              :options="questionOptions"
            />
          </validation-provider>
        </div>
      </div>

      <div class="mt-4">
        <training-document
          ref="documents"
          :is-read-only="isReadOnly"
          :items="documents"
        />
      </div>
    </validation-observer>
  </div>
</template>
<script>
import PageHeader from 'PageHeader';
import SectionTitle from 'SectionTitle';
import CustomDatePicker from 'custom-date-picker';
import CustomTimePicker from 'custom-time-picker';
import CustomCallout from 'custom-callout';
import CustomDropDown from 'custom-drop-down';
import CustomInput from 'CustomInput';
import ActionButton from 'ActionButton';
import {
  _getTrainningDetail,
  _isInvoiceRelated,
  getContractActivities,
  _getDurations,
  getSpecialists,
  _getParticipantTypes,
  _getDocumentTypes,
  _saveTraining,
  _deleteTraining,
  _isCostNeeded,
} from '@/modules/trainingHistory/api';
import { getStudentDetail as _getStudentDetail } from '@/modules/shared/provider.api';
import Checkbox from 'CheckBox';
import alert from 'utils/mixins/alert.mixin';
import { Documents as TrainingDocument } from './components';
import { serialize as objectToFormData } from 'object-to-formdata';
import moment from 'moment';
import CustomTextArea from 'CustomTextArea';
import Error from 'custom-error';

import { getMunicipalities as _getMunicipalities } from '@/modules/shared/location.api';
import CustomRadio from 'custom-radio';

const activitiesEnum = {
  AereoViequesCulebra: 1763,
  Charla: 1758,
  Compu: 1757,
  CompuMeeting: 1850,
  CompuMeetingABA: 1851,
  ConsultABA: 1765,
  MaritimoViequesCulebra: 1764,
  ServicioEmergencia: 1766,
  Taller: 1756,
  TransportacionAutoViequesCulebra: 1824,
  PagoEstacionamientoPRParaIrAViequesCulebra: 1825,
};

export default {
  name: 'TrainingDetails',
  components: {
    CustomRadio,
    PageHeader,
    SectionTitle,
    CustomDatePicker,
    CustomDropDown,
    CustomInput,
    CustomTimePicker,
    Checkbox,
    TrainingDocument,
    CustomCallout,
    CustomTextArea,
    Error,
    ActionButton,
  },
  mixins: [alert],
  props: {
    id: {
      type: [Number, String],
      default: 0,
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    model: {
      date: null,
      selectedActivity: null,
      selectedMunicipality: null,
      place: '',
      startHour: null,
      selectedDuration: null,
      selectedSpecialist: null,
      comment: '',
      selectedStudentSie: '',
      cost: null,
      studentAssistance: null,
    },
    showCost: false,
    details: {},
    selectedStudent: null,
    student: {
      studentId: null,
      fullName: null,
      age: null,
      regNumber: null,
    },
    searchStudentSie: null,
    activities: [],
    municipalities: [],
    allMunicipalities: [],
    specialists: [],
    durations: [],
    participants: [],
    documents: [],
    isInvoiceRelated: false,
    questionOptions: [
      {
        name: 'answer1',
        label: 'Sí',
        value: true,
      },
      {
        name: 'answer2',
        label: 'No',
        value: false,
      },
    ],
  }),
  computed: {
    isCompuActivity() {
      return [
        activitiesEnum.Compu,
        activitiesEnum.CompuMeeting,
        activitiesEnum.CompuMeetingABA,
      ].includes(this.model.selectedActivity?.id);
    },
    currentYearId() {
      return this.currentAcademicYear().id;
    },
    companyId() {
      return this.$store.state['$_user'].user.profile.companyid;
    },
    pageHeaderConfig() {
      return {
        title: 'Historial de Adiestramiento, Taller y COMPU',
        icon: 'fas fa-user-tie',
        routes: ['Proveedor', 'Historial de Adiestramiento, Taller y COMPU'],
      };
    },
    headerActionConfig() {
      return [
        {
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left',
          variant: 'outline-success',
          click: () => {
            this.$router.go(-1);
          },
        },
        {
          text: 'Guardar',
          icon: 'fas fa-save',
          variant: 'success',
          click: this.saveHandler,
          visible: this.isAdding || this.isEditMode,
        },
      ];
    },
    isAdding() {
      return this.id == 0;
    },
    isReadOnly() {
      return this.id > 0 && !this.isEditMode;
    },
    hasBilling() {
      return this.isInvoiceRelated;
    },
    showMunicipality() {
      return [
        activitiesEnum.MaritimoViequesCulebra,
        activitiesEnum.TransportacionAutoViequesCulebra,
        activitiesEnum.PagoEstacionamientoPRParaIrAViequesCulebra,
        activitiesEnum.AereoViequesCulebra,
        activitiesEnum.ServicioEmergencia,
        activitiesEnum.ConsultABA,
        activitiesEnum.Taller,
        activitiesEnum.Charla,
        activitiesEnum.Compu,
        activitiesEnum.CompuMeeting,
        activitiesEnum.CompuMeetingABA,
      ].includes(this.model.selectedActivity?.id);
    },
    showPlace() {
      return [
        activitiesEnum.ServicioEmergencia,
        activitiesEnum.ConsultABA,
        activitiesEnum.Taller,
        activitiesEnum.Charla,
        activitiesEnum.Compu,
        activitiesEnum.CompuMeeting,
        activitiesEnum.CompuMeetingABA,
      ].includes(this.model.selectedActivity?.id);
    },
    showHour() {
      return [
        activitiesEnum.ServicioEmergencia,
        activitiesEnum.Taller,
        activitiesEnum.Charla,
        activitiesEnum.Compu,
        activitiesEnum.CompuMeeting,
        activitiesEnum.CompuMeetingABA,
      ].includes(this.model.selectedActivity?.id);
    },
    showDuration() {
      return [
        activitiesEnum.ServicioEmergencia,
        activitiesEnum.Taller,
        activitiesEnum.Charla,
        activitiesEnum.Compu,
        activitiesEnum.CompuMeeting,
        activitiesEnum.CompuMeetingABA,
        activitiesEnum.ConsultABA,
      ].includes(this.model.selectedActivity?.id);
    },
    showSpecialist() {
      return [
        activitiesEnum.MaritimoViequesCulebra,
        activitiesEnum.TransportacionAutoViequesCulebra,
        activitiesEnum.PagoEstacionamientoPRParaIrAViequesCulebra,
        activitiesEnum.AereoViequesCulebra,
        activitiesEnum.ServicioEmergencia,
        activitiesEnum.ConsultABA,
        activitiesEnum.Taller,
        activitiesEnum.Charla,
        activitiesEnum.Compu,
        activitiesEnum.CompuMeeting,
        activitiesEnum.CompuMeetingABA,
      ].includes(this.model.selectedActivity?.id);
    },
    showComment() {
      return [
        activitiesEnum.MaritimoViequesCulebra,
        activitiesEnum.TransportacionAutoViequesCulebra,
        activitiesEnum.PagoEstacionamientoPRParaIrAViequesCulebra,
        activitiesEnum.AereoViequesCulebra,
        activitiesEnum.ServicioEmergencia,
        activitiesEnum.ConsultABA,
        activitiesEnum.Taller,
        activitiesEnum.Charla,
        activitiesEnum.Compu,
        activitiesEnum.CompuMeeting,
        activitiesEnum.CompuMeetingABA,
      ].includes(this.model.selectedActivity?.id);
    },
    showParticipants() {
      return [activitiesEnum.Taller, activitiesEnum.Charla].includes(
        this.model.selectedActivity?.id
      );
    },
    showStudentSearch() {
      return [
        activitiesEnum.Compu,
        activitiesEnum.CompuMeeting,
        activitiesEnum.CompuMeetingABA,
        activitiesEnum.ConsultABA,
      ].includes(this.model.selectedActivity?.id);
    },
    participantsHasSelected() {
      return this.paricipants?.filter((p) => p.selected == true).length > 0;
    },
  },
  watch: {
    async 'model.date'(newValue) {
      if (newValue) await this.isCostNeeded();
    },
    async 'model.selectedMunicipality'(newValue) {
      if (newValue?.id > 0) await this.isCostNeeded();
    },
    async 'model.selectedActivity'(newValue) {
      _getDurations(newValue?.id).then(({ data }) => {
        this.durations = data;
        this.model.selectedDuration = this.details
          ? this.durations.find((x) => x.id == this.details.durationId)
          : null;
      });

      if (
        [
          activitiesEnum.MaritimoViequesCulebra,
          activitiesEnum.TransportacionAutoViequesCulebra,
          activitiesEnum.PagoEstacionamientoPRParaIrAViequesCulebra,
          activitiesEnum.AereoViequesCulebra,
        ].includes(newValue?.id)
      ) {
        this.municipalities = this.allMunicipalities.filter(
          (f) => f.id == 75 || f.id == 25
        );
      } else {
        this.municipalities = this.allMunicipalities;
      }
      if (newValue?.id > 0) {
        await this.isCostNeeded();
      }
    },
  },
  async mounted() {
    await _getMunicipalities({ AcademicYearId: this.currentYearId }).then(
      ({ data }) => {
        this.allMunicipalities = data;
      }
    );
    await getSpecialists(this.currentYearId, this.companyId).then(
      ({ data }) => {
        this.specialists = data;
      }
    );
    await _getParticipantTypes().then(({ data }) => {
      this.participants = data;
    });

    if (this.id > 0) {
      this.getDetails();
      _isInvoiceRelated(this.id).then((res) => {
        this.isInvoiceRelated = res.data;
      });
    }
  },
  methods: {
    async getDetails() {
      const { data } = await _getTrainningDetail(this.id);
      this.details = data;
      this.model.date = this.details.date;
      this.onDateChange(this.model.date);

      this.model.selectedMunicipality = this.allMunicipalities.find(
        (x) => x.id == this.details.municipalityId
      );
      this.model.place = this.details.location || null;

      const hourStartFormat = moment()
        .set({
          hour: this.details.timeStart.hours,
          minute: this.details.timeStart.minutes,
        })
        .format('HH:mm zz');
      this.model.startHour = hourStartFormat || null;

      this.model.selectedSpecialist = await this.specialists.find(
        (x) => x.id == this.details.specialistId
      );

      //Participants
      await this.details.participants.forEach((p) => {
        this.participants.find((f) => f.id == p).selected = true;
      });

      this.model.comment = this.details.comment;
      this.model.cost = this.details.cost;

      //student
      this.selectedStudent = this.details.student ?? null;
      if (this.details.studentAssistance !== null) {
        this.model.studentAssistance = this.details.studentAssistance
          ? this.questionOptions[0]
          : this.questionOptions[1];
      }

      //documents
      this.details.documents.forEach((d) => {
        this.documents.push({
          documentTypeName: d.documentTypeName,
          documentName: d.documentName,
          documentTypeId: d.documentTypeId,
          isRequired: d.isRequired,
          file: d.file,
          documentId: d.documentId,
          visible: true,
        });
      });
    },
    async saveHandler() {
      const isValid = await this.$refs.trainingDetailsForm.validate();
      if (!isValid) {
        this.ShowToast(
          '',
          'Favor completar los campos para continuar.',
          'error'
        );
        return;
      }

      let participants = [];
      if (this.showParticipants) {
        this.participants
          .filter((f) => f.selected == true)
          .forEach((p) => {
            participants.push(p.id);
          });
      }

      let files = new FormData();

      let documentToSave = [];
      await this.documents.forEach((p) => {
        documentToSave.push({
          DocumentId: p.documentId,
          documentTypeId: p.documentTypeId,
        });
      });
      let saveModel = objectToFormData(
        {
          id: this.id > 0 ? this.id : null,
          date: this.model.date,
          activityId: this.model.selectedActivity?.id,
          municipalityId: this.model.selectedMunicipality?.id,
          location: this.model.place,
          timeStartFormatted: this.model.startHour,
          durationId: this.model.selectedDuration?.id,
          specialistId: this.model.selectedSpecialist?.id,
          comment: this.model.comment,
          files: this.documents,
          documents: documentToSave,
          participants: this.showParticipants ? participants : null,
          student: this.selectedStudent,
          cost: this.model.cost,
          studentAssistance: this.model?.studentAssistance?.value,
        },
        { indices: true, nullsAsUndefineds: true }
      );

      const { data } = await _saveTraining(saveModel, files).catch((error) =>
        this.ShowToast('Error', error?.response?.data?.message, 'error')
      );

      data.forEach((v) => {
        if (v.isValid) {
          this.ShowToast(
            'Proceso Exitoso',
            'Datos guardados satisfactoriamente.',
            'success'
          );

          this.$router.push({ name: 'TrainingHistory' });
        } else {
          this.Toast('Alerta', v.message, 'error', 10000);
        }
      });
    },
    async onSearchStudentHandler() {
      const isValid = await this.$refs.studentSearch.validate();
      if (!isValid) {
        this.ShowToast(
          '',
          'Favor, debe completar los campos para continuar.',
          'error'
        );
        return;
      }

      _getStudentDetail(this.searchStudentSie).then(({ data }) => {
        this.student.studentId = data.studentId;
        this.student.fullName = data.mipeStudent.fullName;
        this.student.age = parseInt(data.age);
        this.student.regNumber = data.mipeStudent.regStudentId.replace('-', '');
      });
    },
    async onDeleteHandler() {
      const answer = await this.ShowCancelModal(
        'Atención',
        '¿Está seguro que desea eliminar la actividad?',
        'Si',
        'No'
      );
      if (answer) {
        await _deleteTraining(this.id)
          .then(() => {
            this.ShowToast('', 'Acitivad eliminada exitosamente.', 'success');
            this.$router.push({ name: 'TrainingHistory' });
          })
          .catch(() =>
            this.ShowToast(
              '',
              'No ha sido posible eliminar la actividad.',
              'error'
            )
          );
      }
    },
    clearData() {
      this.model.selectedMunicipality = null;
      this.model.selectedSpecialist = null;
      this.model.selectedDuration = null;
      this.documents = [];
      this.model.activities?.forEach((a) => {
        a.selected = false;
      });
      this.selectedStudent = null;
    },
    async isCostNeeded() {
      const model = {
        activityId: this.model.selectedActivity?.id,
        date: this.model.date ?? '',
        municipalityId: this.model.selectedMunicipality?.id,
        companyId: this.companyId,
        durationId: null,
      };
      if (
        !model.activityId > 0 ||
        model.date.length == 0 ||
        !model.municipalityId > 0 ||
        !model.companyId > 0
      ) {
        this.showCost = false;
        return;
      }

      const { data: showCost } = await _isCostNeeded(model);
      this.showCost = showCost;
    },
    async onActivityChange(activity) {
      await this.clearData();
      await _getMunicipalities({ AcademicYearId: this.currentYearId }).then(
        ({ data }) => {
          this.allMunicipalities = data;
        }
      );

      if (
        [
          activitiesEnum.MaritimoViequesCulebra,
          activitiesEnum.TransportacionAutoViequesCulebra,
          activitiesEnum.PagoEstacionamientoPRParaIrAViequesCulebra,
          activitiesEnum.AereoViequesCulebra,
        ].includes(activity?.id)
      ) {
        this.municipalities = this.allMunicipalities.filter(
          (f) => f.id == 75 || f.id == 25
        );
      } else {
        this.municipalities = this.allMunicipalities;
      }

      await _getDocumentTypes(activity?.id).then((res) => {
        res.data.forEach((d) => {
          this.documents.push({
            documentTypeName: d.documentTypeName,
            documentName: d.documentName,
            documentTypeId: d.documentTypeId,
            isRequired: d.isRequired,
            file: d.file,
            documentId: d.documentId,
            visible: true,
          });
        });
      });
    },
    async onDateChange(date) {
      await this.clearData();
      this.activities = [];
      this.model.selectedActivity = null;

      await getContractActivities(
        moment(date)
          .locale('es-PR')
          .format('YYYY/MM/DD')
      ).then(({ data }) => {
        this.activities = data;
        if (this.details.durationId) {
          this.model.selectedActivity = this.activities.find(
            (x) => x.id == this.details.activityId
          );
        }
      });
    },
    async onStudentSelection(student) {
      if (this.selectedStudent?.studentId == student.studentId) {
        return;
      }

      if (!this.selectedStudent) {
        this.selectedStudent = student;
        return;
      }

      const answer = await this.ShowCancelModal(
        'Atención',
        '¿Está seguro que desea reemplazar el estudiante seleccionado?',
        'Si',
        'No'
      );
      if (answer) {
        this.model.studentAssistance = null;
        this.selectedStudent = student;
      }
    },
    async onStudentSelectionDelete() {
      const answer = await this.ShowCancelModal(
        'Atención',
        '¿Está seguro que desea eliminar la selección de estudiante?',
        'Si',
        'No'
      );
      if (answer) {
        this.selectedStudent = null;
      }
    },
    async cancelHandler() {
      const answer = await this.ShowCancelModal(
        'Atención',
        `¿Está seguro que desea cancelar los datos introducidos?`,
        'Si',
        'No'
      );
      if (answer) {
        this.documents = [];
        await this.getDetails();
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/shared/scss/config/_colors.scss';

.custom-input__extended ::v-deep {
  .input-group-text {
    background: map-get($colors, 'primary-1') !important;
    color: map-get($colors, 'white') !important;
    border-end-end-radius: 4px !important;
  }
}

.custom-radio__extended ::v-deep {
  .custom-radio-container {
    .radio-button {
      display: flex;
      align-items: center;
      border: unset;
      background: transparent;
      margin-right: 30px;
      .rounded-circle {
        margin-right: 10px;
      }
    }
    .bg-checked {
      background: transparent !important;
      .rounded-circle {
        background: map-get($colors, 'secondary-22');
        i {
          & {
            color: map-get($colors, 'white');
          }
        }
      }
    }
    .error {
      background-color: unset !important;
      .rounded-circle {
        border-color: map-get($colors, 'red') !important;
      }
    }
  }
}
</style>
